import React, { useState, useEffect, Fragment, useCallback } from "react";
import { Accordion } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanyFilters } from "../../../store/actions/rateCard";
import { getAllSellersList } from "../../../store/actions";
import { getLedgerEntryByIdService } from "../../../store/actions/inventory.action";
import { createLedger, GetAllLedgerCurrency, GetAllLedgerPaymentMode, getAllLedgerStatus, getAllLedgerTransactionMode, GetAllLedgerTransactionType, updateLedger, updateLedgerAttachments } from "../../../store/actions/accounts.action";
import { Tooltip as WmsTooltip } from "react-tooltip";
import { toast } from "react-toastify";
import { allowedDocExtensions } from "../../../constant";
import _ from "lodash";
import { handleDownloadAllFiles, handleDownloadFile } from "../../../store/actions/picker.action";
import LoaderSpinner from "../../common/Loader";
import Select from "react-select";
import { getSelectList } from "../../../config/utils";
import InlineFilePreview from "../../common/PreviewAttachment";
import DatePickerComponent from "../../common/DatePickerComponent";
import PropTypes from 'prop-types';

/* istanbul ignore next */
// Helper component for rendering file attachments with URLs
const FileAttachmentWithUrl = ({ item, index, isEditable, handleDeleteAttachment, handleDownload }) => (
    <div className="selectedFile mr-1" key={item?.id}>
        <div className="fileName">
            {/\.(jpg|jpeg|png|gif|webp)$/i.test(item.name) ? (
                <img
                src={item.fileUrl}
                alt={item.name}
                style={{ width: "100px", height: "auto", borderRadius: "8px" }}
                />
            ) : (
                <a
                href={item.fileUrl}
                target="_blank"
                rel="noreferrer"
                >
                {item.name || item.documentName}
                </a>
            )}
        </div>
        
        {isEditable && (
            <button
                className="deleteFile"
                onClick={() => handleDeleteAttachment(item, index)}
                disabled={!isEditable}
            >
                <i className="fa fa-times" aria-hidden="true"></i>
            </button>
        )}
        <button
            className="downBtn ml-2"
            onClick={() => handleDownload(item)}
        >
            <i className="fa fa-download" aria-hidden="true"></i>
        </button>
    </div>
);
FileAttachmentWithUrl.propTypes = {
    item: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    isEditable: PropTypes.bool.isRequired,
    handleDeleteAttachment: PropTypes.func.isRequired,
    handleDownload: PropTypes.func.isRequired,
};
// Helper component for rendering file attachments without URLs (new files)
const FileAttachmentWithoutUrl = ({ item, index, isEditable, handleDeleteAttachment, handleDownload }) => (
    <div className={`selectedFile mr-2 filePreview`} key={item?.id}>
        <div className="fileName">
            <div>
                {item.name}
                <InlineFilePreview
                    file={item}
                    onClose={() => handleDeleteAttachment(item, index)}
                />
            </div>
        </div>
        {isEditable && item.fileUrl && (
            <button
                className="deleteFile"
                onClick={() => handleDeleteAttachment(item, index)}
                disabled={!isEditable}
            >
                <i className="fa fa-times" aria-hidden="true"></i>
            </button>
        )}
        {item.fileUrl && (
            <button
                className="downBtn ml-2"
                onClick={() => handleDownload(item)}
            >
                <i className="fa fa-download" aria-hidden="true"></i>
            </button>
        )}
    </div>
);
FileAttachmentWithoutUrl.propTypes = {
    item: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    isEditable: PropTypes.bool.isRequired,
    handleDeleteAttachment: PropTypes.func.isRequired,
    handleDownload: PropTypes.func.isRequired,
};
// Helper component for rendering all attachments
const AttachmentsSection = ({ formData, isEditable, maxSize, handleAttachments, handleDownloadAll, handleDeleteAttachment, handleDownload }) => {
    const hasFileUrls = formData.attachments.filter(item => item.fileUrl).length > 0;
    const attachmentsWithUrls = formData.attachments.filter(item => !item.isDeleted && item.fileUrl);
    const attachmentsWithoutUrls = formData.attachments.filter(item => !item.isDeleted && !item.fileUrl);

    return (
        <div>
            <div>
                <div className="input-label-txt">
                    Attachments <i id="uploadInfo" className="fa fa-info-circle" aria-hidden="true"></i>
                </div>
                <WmsTooltip
                    anchorId={`uploadInfo`}
                    content={`Upload files to attach to this record. You can upload multiple files here. Max file size is ${maxSize}MB.`}
                    place="top"
                    className="tooltipTxt"
                />
            </div>
            <div className="uploadContainer">
                <div className="inlineBlock fileUpload inventory">
                    <button
                        type="button"
                        className="btn cus-seconday-bg-btn"
                        disabled={!isEditable}
                    >
                        Upload documents
                    </button>
                    <input
                        className="hiddenFileField"
                        type="file"
                        id="customFile"
                        multiple
                        onChange={handleAttachments}
                        disabled={!isEditable}
                    />
                </div>
                <div>
                    {hasFileUrls && (
                        <button className='btn mt-1 mr-2 downloadAll' onClick={handleDownloadAll}>
                            <i className="fa fa-download" aria-hidden="true"></i> Download All
                        </button>
                    )}
                </div>
                <div>
                    {attachmentsWithUrls.map((item, index) => (
                        <Fragment key={item?.id}>
                            <FileAttachmentWithUrl
                                item={item}
                                index={index}
                                isEditable={isEditable}
                                handleDeleteAttachment={handleDeleteAttachment}
                                handleDownload={handleDownload}
                            />
                        </Fragment>
                    ))}
                </div>
                <div className='displayFlex alignItemsCenter flexWrap'>
                    {attachmentsWithoutUrls.map((item, index) => (
                        <Fragment key={item?.id}>
                            <FileAttachmentWithoutUrl
                                item={item}
                                index={index}
                                isEditable={isEditable}
                                handleDeleteAttachment={handleDeleteAttachment}
                                handleDownload={handleDownload}
                            />
                        </Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};
AttachmentsSection.propTypes = {
    formData: PropTypes.object.isRequired,
    isEditable: PropTypes.bool.isRequired,
    maxSize: PropTypes.number.isRequired,
    handleAttachments: PropTypes.func.isRequired,
    handleDownloadAll: PropTypes.func.isRequired,
    handleDeleteAttachment: PropTypes.func.isRequired,
    handleDownload: PropTypes.func.isRequired,
};
// Helper component for conversion amount display
const ConversionAmountDisplay = ({ formData }) => {
    const shouldShowConversion = Boolean(formData.baseConversionAmount && formData?.conversionRate);
    const isBaseCurrency = Number(formData?.baseCurrencyId) === 1;
    const calculatedAmount = isBaseCurrency
        ? formData?.conversionRate * formData?.baseConversionAmount
        : (formData?.baseConversionAmount / formData?.conversionRate).toFixed(2);

    return shouldShowConversion ? (
        <span className="mt-1">
            {formData?.baseConversionAmount} {'/'} {formData?.conversionRate} =
            {' $ '}{calculatedAmount}
        </span>
    ) : null;
};
ConversionAmountDisplay.propTypes = {
    formData: PropTypes.object.isRequired,
};
/* istanbul ignore next */
const LedgerUpdate = () => {
    const location = useLocation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    const [maxSize] = useState(10);
    const [isEditable, setIsEditable] = useState(location?.state?.editable);
    const [isAddEntry] = useState(location?.state?.isAddEntry);
    const { showAccountingMenu, transactionsModeList, transactionTypeList, ledgerCurrencyList, ledgerPaymentModeList, ledgerStatusList } = useSelector((state) => state.accountingReducer);
    const { filterCompanies } = useSelector((state) => state.RateCardReducer);
    const [error, setErrors] = useState({});
    const [formData, setFormData] = useState({
        attachments: [],
        description: '',
        baseCurrencyId: 1,
        conversionRate: 1,
        entryDate: new Date(),
    });

    const handleResponseData = (data) => {
        if(isAddEntry) {
            let payloadData = {...data, description: '', note: '', attachments: [], baseConversionAmount: '', paymentModeId: ''};
            setFormData(payloadData);
        } else {
            setFormData(data);
        }
    };

    const fetchData = useCallback(async (id) => {
        try {
            setShowLoader(true);
            const res = await getLedgerEntryByIdService(id);
            if (res?.data?.Status === 200) {
                handleResponseData(res.data.Entity);
            } else {
                navigate('/accounting/ledger');
            }
            setShowLoader(false);
        } catch (err) {
            console.log(err);
        }
    }, [navigate]);

    const fetchSellers = useCallback(async (activeSellerList) => {
        let data = {
            companyIds: [activeSellerList],
        };
        let res = await getAllSellersList(data);
        if (res?.data?.Status === 200) {
            setSellerList(getSelectList(res?.data?.Entity?.listSellerMasterOrdersEntity, 'id', 'newSellerName'));
        };
    }, []);

    // Helper function to initialize data fetching
    const initializeDataFetching = useCallback(() => {
        if (transactionsModeList.length === 0) {
            dispatch(getAllLedgerTransactionMode());
        }
        if (ledgerCurrencyList.length === 0) {
            dispatch(GetAllLedgerCurrency());
        }
        if (ledgerStatusList.length === 0) {
            dispatch(getAllLedgerStatus());
        }
        if (ledgerPaymentModeList.length === 0) {
            dispatch(GetAllLedgerPaymentMode());
        }
        if (transactionTypeList.length === 0) {
            dispatch(GetAllLedgerTransactionType());
        }
    }, [dispatch, transactionsModeList, ledgerCurrencyList, ledgerStatusList, ledgerPaymentModeList, transactionTypeList]);

    // Helper function to handle company selection effects
    const handleCompanySelectionEffect = useCallback(() => {
        if (formData?.entryCompanyId) {
            let companyData = formData?.entryCompanyId.Id;
            fetchSellers(companyData);
        }
    }, [formData?.entryCompanyId, fetchSellers]);

    // Helper function to initialize data when ID changes
    const initializeDataById = useCallback(() => {
        if (Number(id) > 0) {
            let data = {
                pageIndex: 1,
                pageSize: 10,
                sortColumn: "name",
                sortOrder: "ascending",
            };
            dispatch(fetchCompanyFilters(data));
            fetchData(id);
        }
    }, [id, dispatch, fetchData]);

    // Helper function to setup company list
    const setupCompanyList = useCallback(() => {
        if (filterCompanies?.Entity?.listCompanyMasterOrdersEntity?.length) {
            setCompanyList(getSelectList(filterCompanies?.Entity?.listCompanyMasterOrdersEntity, 'Id', 'name'));
        } else {
            dispatch(fetchCompanyFilters({ screenType: 2 }));
        }
    }, [filterCompanies, dispatch]);

    useEffect(handleCompanySelectionEffect, [handleCompanySelectionEffect]);
    useEffect(initializeDataFetching, [initializeDataFetching]);
    useEffect(initializeDataById, [initializeDataById]);
    useEffect(setupCompanyList, [setupCompanyList]);

    const handleBack = () => {
        navigate('/accounting/ledger');
    };

    const handleInputBlur = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value.trim() });
        setErrors({ ...error, [name]: '' });
    };

    const handleTransactionModeChange = (value) => {
        setFormData({ ...formData, transactionModeId: value, credit: '', debit: '' });
        setErrors({ ...error, transactionModeId: '' });
    };

    const handleBaseCurrencyChange = (value) => {
        if (Number(value) === 1) {
            setFormData({ ...formData, baseCurrencyId: value, conversionRate: 1 });
            setErrors({ ...error, baseCurrencyId: '', conversionRate: '' });
        } else {
            setFormData({ ...formData, baseCurrencyId: value, conversionRate: '' });
            setErrors({ ...error, baseCurrencyId: '' });
        }
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;

        if (name === "transactionModeId") {
            handleTransactionModeChange(value);
            return;
        }

        if (name === "baseCurrencyId") {
            handleBaseCurrencyChange(value);
            return;
        }

        setFormData({ ...formData, [name]: value });
        setErrors({ ...error, [name]: '' });
    };

    // Helper function to validate attachment files
    const validateAttachmentFiles = (files) => {
        if (files.length === 0) return { isValid: false, validFiles: [] };

        if (files.length > 5) {
            toast.info(`Only ${5} files can be uploaded at one time`, 1000);
            return { isValid: false, validFiles: [] };
        }

        let validFiles = [];
        let totalSizeBytes = 0;

        files.forEach((file) => {
            const extension = file.name.split(".").pop()?.toLowerCase();
            const fileSizeBytes = file.size;

            if (!allowedDocExtensions.includes(extension)) {
                toast.error(`Unsupported file format: ${file.name}`, 1000);
                return;
            }
            totalSizeBytes += fileSizeBytes;
            file.isNew = true;
            validFiles.push(file);
        });

        if (totalSizeBytes > maxSize * 1024 * 1024) {
            toast.info(`Total file size should not exceed ${maxSize}MB`, 1000);
            return { isValid: false, validFiles: [] };
        }

        return { isValid: validFiles.length > 0, validFiles };
    };

    const handleAttachments = (e) => {
        let files = [...e.target.files];
        try {
            const { isValid, validFiles } = validateAttachmentFiles(files);

            if (!isValid) {
                e.target.value = "";
                return;
            }

            if (validFiles.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    attachments: [...prev.attachments, ...validFiles],
                }));
            }

            e.target.value = "";
        } catch (err) {
            console.log(err);
        }
    };

    const validateForm = () => {
        let isValid = true;
        let errors = {};
        if (!formData.entryCompanyId) {
            isValid = false;
            errors.entryCompanyId = "Company is required";
        }

        if (!formData.entrySellerId) {
            isValid = false;
            errors.entrySellerId = "Seller is required";
        }

        if (!formData.transactionModeId) {
            isValid = false;
            errors.transactionModeId = "Transaction Mode is required";
        }

        if (formData.transactionModeId && Number(formData.transactionModeId) === 1) {
            if (!formData.paymentModeId) {
                isValid = false;
                errors.paymentModeId = "Payment Mode is required";
            }
        }

        if (!formData.transactionTypeId) {
            isValid = false;
            errors.transactionTypeId = "Transaction Type is required";
        }
        if (!formData.statusId) {
            isValid = false;
            errors.statusId = "Status is required";
        }
        if (!formData.description || formData.description.trim() === '') {
            isValid = false;
            errors.description = "Description is required";
        }

        if (!formData.conversionRate) {
            isValid = false;
            errors.conversionRate = "Conversion Rate is required";
        }
        if (!formData.baseConversionAmount) {
            isValid = false;
            errors.baseConversionAmount = "Conversion Amount is required";
        }

        if (!formData.baseCurrencyId) {
            isValid = false;
            errors.baseCurrencyId = "Currency field is required";
        }

        !isValid && toast.error("Please fill all required fields");
        setErrors(errors);
        return isValid;
    }

    const handleSaveAttachments = async (ledgerId) => {
        const formDataToSend = new FormData();
        let newFiles = formData.attachments.filter(file => file.isNew);
        newFiles.forEach(file => {
            formDataToSend.append('files', file);
        });
        formDataToSend.append('LedgerEntryId', ledgerId);

        try {
            let response = await updateLedgerAttachments(formDataToSend);
            if (response.status === 200) {
                setShowLoader(false);
                toast.success(Number(id) > 0 ? 'Ledger Updated successfully!' : 'Ledger created successfully!');
                navigate('/accounting/ledger');
            } else {
                setShowLoader(false);
                toast.error('Failed to save attachments. Please try again.');
            }
        } catch (error) {
            setShowLoader(false);
            toast.error('An error occurred while saving attachments.');
        }
    }

    const preparePayloadData = () => {
        const payloadData = { ...formData };
        payloadData.entryCompanyId = formData.entryCompanyId.Id ? formData.entryCompanyId.Id : formData.entryCompanyId;
        payloadData.entrySellerId = formData.entrySellerId.id ? formData.entrySellerId.id : formData.entrySellerId;
        payloadData.transactionModeId = Number(formData.transactionModeId);
        payloadData.transactionTypeId = Number(formData.transactionTypeId);
        payloadData.baseCurrencyId = formData.baseCurrencyId ? Number(formData.baseCurrencyId) : null;
        payloadData.baseCurrencyId = formData.baseCurrencyId ? Number(formData.baseCurrencyId) : null;
        if (isAddEntry) {
            payloadData.id = 0;
        } else if (Number(id) > 0) {
            payloadData.id = Number(id);
        } else {
            payloadData.id = 0;
        }
        
        if (Number(id) === 0) {
            payloadData.id = 0;
        }

        return payloadData;
    };

    const saveLedgerData = async (payloadData) => {
        if (Number(id) > 0 && Boolean(!isAddEntry)) {
            return await updateLedger(payloadData);
        }
        return await createLedger(payloadData);
    };

    const handleSuccessfulSave = (response) => {
        const newAttachments = formData.attachments.filter(file => file.isNew);
        const ledgerId = Number(id) > 0 ? id : response.data.Entity;

        if (newAttachments.length > 0) {
            handleSaveAttachments(ledgerId);
        } else {
            setShowLoader(false);
            const successMessage = Number(id) > 0 ? 'Ledger Updated successfully!' : 'Ledger created successfully!';
            toast.success(successMessage);
            navigate('/accounting/ledger');
        }
    };

    const handleSaveLedger = async () => {
        if (!validateForm()) {
            return;
        }
        try {
            setShowLoader(true);
            const payloadData = preparePayloadData();
            const response = await saveLedgerData(payloadData);

            if (response.data.Status === 200) {
                handleSuccessfulSave(response);
            } else {
                setShowLoader(false);
                const errorMessage = response.data.Message?.AppStatusDescription || 'Failed to save ledger. Please try again.';
                toast.error(errorMessage);
            }
        } catch (error) {
            setShowLoader(false);
            toast.error('An error occurred while saving the ledger.');
        }
    };

    const handleDeleteAttachment = (item, index) => {
        let formDataClone = _.cloneDeep(formData);
        if (item.isNew) {
            formDataClone.attachments.splice(index, 1);
        } else {
            formDataClone.attachments[index].isDeleted = true;
            formDataClone.attachmentActions = formDataClone.attachmentActions ?? [];
            formDataClone.attachmentActions.push(formDataClone.attachments[index]);
            formDataClone.attachments.splice(index, 1);
        }
        setFormData(formDataClone);
    };

    const handleDownloadAll = async () => {
        let oldAttachments = formData.attachments.filter(file => !file.isNew);
        let fileUrls = _.map(oldAttachments, 'fileUrl');
        let response = await handleDownloadAllFiles(fileUrls);
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers['content-disposition'];
            let filename = `Attachments_${new Date().toISOString()}.zip`;
            if (disposition && disposition.indexOf('filename=') !== -1) {
                filename = disposition
                    .split('filename=')[1]
                    .split(';')[0]
                    .replace(/"/g, ''); //NOSONAR
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); //NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    };

    const handleDownload = async (item) => {
        let response = await handleDownloadFile(encodeURIComponent(item.fileUrl));
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', item.documentName || item.name);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); //NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    };

    const handleMultiSelect = (value, fieldName) => {
        setFormData({
            ...formData,
            [fieldName]: value
        });
    };

    const handleDateFieldChange = (date) => {
        setFormData({
            ...formData,
            entryDate: date
        });
    };

    return (
        <div className={` ${showAccountingMenu ? 'col-10' : 'col-12'}`}>
            <div>
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>
                        {id > 0 ? "Update" : "Create"}
                        {showLoader && <span className="ml-2"><LoaderSpinner /></span>}
                    </span>
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            <button
                                className="btn cus-primary-transparent-btn mr-2"
                                onClick={handleBack}
                                disabled={showLoader}
                            >
                                Back
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn mr-2"
                                onClick={() => setIsEditable(true)}
                                disabled={showLoader || isEditable}
                            >
                                Edit
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn"
                                onClick={handleSaveLedger}
                                disabled={showLoader || !isEditable}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>

                <div className="cus-page-content-sec">
                    <div className="incomingAccordion accordion">
                        <div className="row fontFix">
                            <div className="col-lg-12">
                                <Accordion
                                    defaultActiveKey={["0", "1"]}
                                    alwaysOpen={true}
                                >
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>
                                            <span>Ledger Details</span>
                                            <i className="fa fa-chevron-down" aria-hidden="true"></i>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="row addressForm">
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="entryCompanyId">Company <span className="error">*</span></label>
                                                    <Select
                                                        closeMenuOnSelect={true}
                                                        options={companyList}
                                                        classNamePrefix="list"
                                                        id='entryCompanyId'
                                                        name="entryCompanyId"
                                                        value={formData?.entryCompanyId}
                                                        onChange={(value) =>
                                                            handleMultiSelect(value, "entryCompanyId")
                                                        }
                                                        placeholder={Number(id) > 0 ? formData?.companyName : "Select Company..."}
                                                        isDisabled={!isEditable || Number(id) > 0}
                                                    />

                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="entrySellerId">Seller <span className="error">*</span></label>
                                                    <Select
                                                        closeMenuOnSelect={true}
                                                        options={sellerList}
                                                        classNamePrefix="list"
                                                        id='entrySellerId'
                                                        name="entrySellerId"
                                                        value={formData?.entrySellerId}
                                                        onChange={(value) =>
                                                            handleMultiSelect(value, "entrySellerId")
                                                        }
                                                        placeholder={Number(id) > 0 ? formData?.sellerName : "Select Seller..."}
                                                        isDisabled={!isEditable || Number(id) > 0}
                                                    />
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="transactionModeId">Transaction Mode <span className="error">*</span></label>
                                                    <select
                                                        className={`form-control transactionModeId ${error.transactionModeId ? 'errorField' : ''}`}
                                                        value={formData?.transactionModeId}
                                                        name="transactionModeId"
                                                        onChange={handleChangeInput}
                                                        disabled={!isEditable || isAddEntry}
                                                        id="transactionModeId"
                                                    >
                                                        <option value="">Select Transaction Mode...</option>
                                                        {transactionsModeList.map((item) => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.transactionMode}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="transactionTypeId">Transaction Type <span className="error">*</span></label>
                                                    <select
                                                        className={`form-control transactionTypeId ${error.transactionTypeId ? 'errorField' : ''}`}
                                                        value={formData?.transactionTypeId}
                                                        name="transactionTypeId"
                                                        onChange={handleChangeInput}
                                                        disabled={!isEditable}
                                                        id="transactionTypeId"
                                                    >
                                                        <option value="">Select Transaction Type...</option>
                                                        {transactionTypeList.map((transaction) => (
                                                            <option key={transaction.id} value={transaction.id}>
                                                                {transaction.transactionType}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="statusId">Status <span className="error">*</span></label>
                                                    <select
                                                        className={`form-control statusId ${error.statusId ? 'errorField' : ''}`}
                                                        value={formData?.statusId}
                                                        name="statusId"
                                                        onChange={handleChangeInput}
                                                        disabled={!isEditable}
                                                        id="statusId"
                                                    >
                                                        <option value="">Select Status...</option>
                                                        {ledgerStatusList.map((status) => (
                                                            <option key={status.id} value={status.id}>
                                                                {status.statusName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="transactionDate">Transaction Date</label>
                                                    <DatePickerComponent
                                                        dateField={formData.entryDate}
                                                        handleDateChange={(date) => handleDateFieldChange(date)}
                                                        fieldName="entryDate"
                                                        dateFormat="MM-dd-yyyy"
                                                        disabled={!isEditable}
                                                        onKeyDown={(e) => {
                                                            e.preventDefault();
                                                        }}
                                                        id="transactionDate"
                                                    />
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="descripField">Description <span className="error">*</span></label>
                                                    <input
                                                        type='text'
                                                        className={`form-control ${error.description ? 'errorField' : ''}`}
                                                        value={formData.description}
                                                        name="description"
                                                        onChange={handleChangeInput}
                                                        onBlur={handleInputBlur}
                                                        disabled={!isEditable}
                                                        id="descripField"
                                                    />
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                                    <AttachmentsSection
                                                        formData={formData}
                                                        isEditable={isEditable}
                                                        maxSize={maxSize}
                                                        handleAttachments={handleAttachments}
                                                        handleDownloadAll={handleDownloadAll}
                                                        handleDeleteAttachment={handleDeleteAttachment}
                                                        handleDownload={handleDownload}
                                                    />
                                                </div>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    <Accordion.Item eventKey="1">
                                        <Accordion.Header>
                                            <span>Additional Details</span>
                                            <i className="fa fa-chevron-down" aria-hidden="true"></i>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="row addressForm">
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="baseCurrencyId">Currency</label>
                                                    <select
                                                        className={`form-control baseCurrencyId ${error.baseCurrencyId ? 'errorField' : ''}`}
                                                        value={formData?.baseCurrencyId}
                                                        name="baseCurrencyId"
                                                        onChange={handleChangeInput}
                                                        disabled={!isEditable}
                                                        id="baseCurrencyId"
                                                    >
                                                        {ledgerCurrencyList.map((currency) => (
                                                            <option key={currency.id} value={currency.id}>
                                                                {currency.currencyName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="conversionRate">Conversion Rate</label>
                                                    <input
                                                        type='number'
                                                        className={`form-control conversionRate ${error.conversionRate ? 'errorField' : ''}`}
                                                        value={formData?.conversionRate}
                                                        name="conversionRate"
                                                        onChange={handleChangeInput}
                                                        disabled={!isEditable || Number(formData?.baseCurrencyId) === 1}
                                                        id="conversionRate"
                                                    />
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="baseConversionAmount">Conversion Amount <span className="error">*</span></label>
                                                    <input
                                                        type='number'
                                                        className={`form-control baseConversionAmount ${error.baseConversionAmount ? 'errorField' : ''}`}
                                                        value={formData?.baseConversionAmount}
                                                        name="baseConversionAmount"
                                                        onChange={handleChangeInput}
                                                        onWheel={(e) => e.target.blur()}
                                                        disabled={!isEditable}
                                                        id="baseConversionAmount"
                                                    />
                                                    <ConversionAmountDisplay formData={formData} />
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="paymentModeId">Payment Mode {Number(formData.transactionModeId) === 1 ? <span className="error">*</span> : ''}</label>
                                                    <select
                                                        className={`form-control paymentModeId ${error.paymentModeId ? 'errorField' : ''}`}
                                                        value={formData?.paymentModeId}
                                                        name="paymentModeId"
                                                        onChange={handleChangeInput}
                                                        disabled={!isEditable}
                                                        id="paymentModeId"
                                                    >
                                                        <option value="">Select Payment Mode...</option>
                                                        {ledgerPaymentModeList.map((payment) => (
                                                            <option key={payment.id} value={payment.id}>
                                                                {payment.paymentMode}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="paymentParty">Payment Party</label>
                                                    <input
                                                        type='text'
                                                        className={`form-control paymentParty ${error.paymentParty ? 'errorField' : ''}`}
                                                        value={formData?.paymentParty}
                                                        name="paymentParty"
                                                        onChange={handleChangeInput}
                                                        onBlur={handleInputBlur}
                                                        disabled={!isEditable}
                                                        id="paymentParty"
                                                    />
                                                    <div className='require-field'>{error.paymentParty}</div>
                                                </div>
                                            </div>
                                            <div className="row addressForm">
                                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                                    <label htmlFor="noteField">Notes</label>
                                                    <textarea
                                                        className="form-control note"
                                                        rows="3"
                                                        disabled={!isEditable}
                                                        value={formData?.note}
                                                        name="note"
                                                        onChange={handleChangeInput}
                                                        onBlur={handleInputBlur}
                                                        id="noteField"
                                                    />
                                                </div>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LedgerUpdate;